/**
 * Markdown → HTML renderer + section parser for the generated premium content.
 * Input comes from our own backend (OpenAI). Rendered inside styled containers.
 */
import { pickFoodImage } from './media';

const escapeHtml = (str) =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inline = (text) =>
  escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>');

const MEAL_LABEL = /^\*\*\s*(PETIT[- ]?D[ÉE]JEUNER|COLLATION[^*]*|D[ÉE]JEUNER|D[ÎI]NER|SNACK[^*]*|BREAKFAST|LUNCH|DINNER)[^*]*\*\*\s*$/i;
// Image markdown autonome : ![alt](https://...)
const IMG_LINE = /^!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)\s*$/;

/**
 * renderMarkdown(md, { mealImages })
 * mealImages: when true, bold meal labels get a decorative dish image.
 */
export const renderMarkdown = (md, opts = {}) => {
  if (!md) return '';
  const { mealImages = false } = opts;
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  let html = '';
  let inUl = false;
  let inOl = false;
  let tableRows = [];

  const flushList = () => {
    if (inUl) { html += '</ul>'; inUl = false; }
    if (inOl) { html += '</ol>'; inOl = false; }
  };
  const flushTable = () => {
    if (tableRows.length) {
      const valid = tableRows.filter((r) => !/^[\s|:-]+$/.test(r));
      html += '<div class="tbl-wrap"><table>';
      valid.forEach((row, idx) => {
        const cells = row.split('|').map((c) => c.trim()).filter((c, i, arr) => !(i === 0 && c === '') && !(i === arr.length - 1 && c === ''));
        const tag = idx === 0 ? 'th' : 'td';
        html += '<tr>' + cells.map((c) => `<${tag}>${inline(c)}</${tag}>`).join('') + '</tr>';
      });
      html += '</table></div>';
      tableRows = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.includes('|') && line.trim().startsWith('|')) {
      flushList();
      tableRows.push(line);
      continue;
    } else {
      flushTable();
    }

    if (IMG_LINE.test(line.trim())) {
      flushList();
      const m = line.trim().match(IMG_LINE);
      html += `<figure class="dish-figure"><img loading="lazy" src="${m[2]}" alt="${escapeHtml(m[1])}"/></figure>`;
    } else if (mealImages && MEAL_LABEL.test(line.trim())) {
      flushList();
      const label = line.trim().replace(/\*\*/g, '');
      html += `<div class="meal-head"><img loading="lazy" src="${pickFoodImage(label)}" alt="${escapeHtml(label)}" class="meal-img"/><span>${inline(label)}</span></div>`;
    } else if (/^\s*([-*_])\1{1,}\s*$/.test(line)) {
      // séparateur horizontal (--- / *** / ___) : ignoré pour un rendu propre
      flushList();
    } else if (/^#{1,6}\s/.test(line)) {
      flushList();
      const level = line.match(/^#+/)[0].length;
      const size = Math.min(level + 1, 4);
      html += `<h${size}>${inline(line.replace(/^#+\s/, ''))}</h${size}>`;
    } else if (/^\s*[-*]\s+/.test(line)) {
      if (!inUl) { flushList(); html += '<ul>'; inUl = true; }
      html += `<li>${inline(line.replace(/^\s*[-*]\s+/, ''))}</li>`;
    } else if (/^\s*\d+\.\s+/.test(line)) {
      if (!inOl) { flushList(); html += '<ol>'; inOl = true; }
      html += `<li>${inline(line.replace(/^\s*\d+\.\s+/, ''))}</li>`;
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      html += `<p>${inline(line)}</p>`;
    }
  }
  flushList();
  flushTable();
  return html;
};

/**
 * Split markdown into top-level sections (title + body markdown).
 * Detects: H1/H2, "N. **TITLE**", and all-caps bold-only lines (no parentheses).
 */
export const parseSections = (md) => {
  if (!md) return [];
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const sections = [];
  let current = null;

  const titleOf = (raw) => {
    const l = raw.trim();
    // H1/H2 : toujours un titre de section
    let m = l.match(/^#{1,2}\s+(.+)$/);
    if (m) return m[1].replace(/\*\*/g, '').replace(/^\d+[.)]\s*/, '').trim();
    // H3 : titre seulement si numéroté (1.) ou tout en majuscules (évite les en-têtes de jour "### Lundi")
    m = l.match(/^###\s+(.+)$/);
    if (m) {
      const rest = m[1].replace(/\*\*/g, '').trim();
      if (/^\d+[.)]/.test(rest) || (/[A-ZÀ-Ÿ]/.test(rest) && rest === rest.toUpperCase())) {
        return rest.replace(/^\d+[.)]\s*/, '').trim();
      }
      return null;
    }
    // "N. **TITRE**"
    m = l.match(/^\d+[.)]\s*\*\*(.+?)\*\*[:：]?\s*$/);
    if (m) return m[1].trim();
    // "**TITRE EN MAJUSCULES**" autonome (sans parenthèses, exclut les libellés de repas)
    m = l.match(/^\*\*([^*()]{4,})\*\*[:：]?\s*$/);
    if (m && /[A-ZÀ-Ÿ]/.test(m[1]) && m[1].trim() === m[1].trim().toUpperCase()) return m[1].trim();
    return null;
  };

  for (const raw of lines) {
    const t = titleOf(raw);
    if (t !== null) {
      if (current) sections.push(current);
      current = { title: t, body: '' };
    } else {
      if (!current) current = { title: '', body: '' };
      current.body += raw + '\n';
    }
  }
  if (current) sections.push(current);
  return sections.filter((s) => s.title || s.body.trim());
};
